import { $byId, isSameOrigin } from "@de-xima/xima-common-js-dom";
import {
  isEmpty,
  notEmpty,
  parseArray,
  parseBoolean,
  parseError,
  parseJsonArray,
  parseJsonObject,
  parseNumber,
  parseRecord,
  parseString,
} from "@de-xima/xima-common-js-lang";
import $ from "jquery";

import { loadingDialog } from "./Notification.ts";
import { remoteCommandParams, runRemoteCommand } from "./Util.ts";

interface IFormStoreConfig {
  /**
   * Part of the path that the link for importing a form from the store must
   * contain. Used to check whether a pasted link is a store form link.
   */
  readonly formPath: string;

  /**
   * Unique ID of the form store.
   */
  readonly id: string;

  /**
   * URL to the form store page.
   */
  readonly url: URL;
}

const KnownFormStores = new Set<IFormStoreConfig>();

function handleErrorGeneric(e: unknown): void {
  console.error("Unexpected error while processing form store", e);
  loadingDialog(false);

  BackendCommand?.handleStoreError?.([
    { name: "params", value: parseError(e).message },
    { name: "type", value: "general" },
  ]).catch((e) => console.error("Cannot show error message", e));
}

function handleErrorDownloadFailure(e: unknown, link: string): void {
  console.error(`Could not download store form from link <${link}>`, e);
  loadingDialog(false);
  BackendCommand?.handleStoreError?.([
    { name: "params", value: link },
    { name: "params", value: parseError(e).message },
    { name: "type", value: "download-error" },
  ]).catch((e) => console.error("Cannot show error message", e));
}

/**
 * Checks whether a link is a link to a store form file. Used when the user
 * copy + pastes a link, to check whether we should attempt to import the form.
 * @param config Configuration for the form store with the URL etc.
 * @param url URL to check.
 * @returns `true` if the given URL is a link to a store form file that returns
 * a ZIP file when a GET request is sent. `false` otherwise.
 */
function isStoreFormFileUrl(config: IFormStoreConfig, url: string): boolean {
  try {
    return url.indexOf(config.formPath) >= 0 && isSameOrigin(url, config.url);
  } catch {
    return false;
  }
}

async function downloadBinaryAsBase64(url: string): Promise<string> {
  const response = await fetch(url, {
    credentials: "include",
    method: "GET",
  });
  if (response.status !== 200) {
    throw new Error(`Server returned status code ${response.status}`);
  }
  const data = await response.blob();
  if (data === undefined) {
    throw new Error("Did not receive a blob");
  }
  return readAsBase64(data);
}

async function readAsBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result;
      resolve(base64 as string);
    };
    reader.onerror = () => {
      reject(reader.error);
    };
    reader.readAsDataURL(blob);
  });
}

/**
 * Handles the form link message form the form store that is sent when the user
 * clicked on the install link. It contains the link to the form to be imported.
 * @param config Configuration for the form store with the URL etc.
 * @param payload Payload received from the store.
 */
async function handleMessageStoreFormLink(config: IFormStoreConfig, payload: unknown): Promise<void> {
  const data = parseRecord(payload);
  const link = parseString(data.link);
  const update = data.update !== undefined ? parseBoolean(data.update) : undefined;
  if (isEmpty(link)) {
    console.warn("No form store link was submitted");
    return;
  }
  await downloadFromLink([config], link, update);
}

/**
 * When the form store was loaded and has signalled that is ready. Informs the
 * store about the forms that already exist in the formcycle application.
 * @param config Configuration for the form store with the URL etc.
 * @param payload Payload received from the store.
 */
function handleMessageStoreReady(config: IFormStoreConfig, payload: unknown): void {
  const iframe = $(".form-store-frame")[0];
  const data = parseRecord(payload);
  const ready = parseBoolean(data.ready);
  if (!(iframe instanceof HTMLIFrameElement) || !ready) {
    return;
  }
  const formData = $(iframe).data("formData") as unknown;
  iframe.contentWindow?.postMessage(
    {
      payload: formData,
      type: "formcycle-form-state",
    },
    config.url.origin,
  );
}

function getFrameByMessageEvent(event: MessageEvent): HTMLIFrameElement | undefined {
  for (const frame of document.getElementsByTagName("iframe")) {
    if (frame.contentWindow === event.source) {
      return frame;
    }
  }
  return undefined;
}

function isSameStore(config: IFormStoreConfig, event: MessageEvent): boolean {
  const frame = getFrameByMessageEvent(event);
  return frame !== undefined && $(frame).data("storeId") === config.id;
}

/**
 * Message handler for messages from the form store. Checks whether the message
 * comes from the form store and has the correct type.
 * @param config Configuration for the form store with the URL etc.
 * @param event Message event received.
 */
async function handleFormStoreMessage(config: IFormStoreConfig, event: MessageEvent): Promise<void> {
  if (!isSameOrigin(config.url, event.origin)) {
    return;
  }
  if (!isSameStore(config, event)) {
    return;
  }

  const data = parseRecord(event.data);
  const type = parseString(data.type);
  switch (type) {
    case "store-ready":
      handleMessageStoreReady(config, data.payload);
      break;
    case "store-form-link":
      await handleMessageStoreFormLink(config, data.payload);
      break;
  }
}

/**
 * Adds the message listener for the messages posted by the form store.
 * @param config Configuration for the form store with the URL etc.
 */
function initFormStoreMessageListener(config: IFormStoreConfig): void {
  window.addEventListener("message", (event) => {
    handleFormStoreMessage(config, event).catch(handleErrorGeneric);
  });
}

/**
 * Import a form from the form store with the given link
 * @param config Configuration for the form store with the URL etc.
 * @param link Link to the form from the from store. Should point to a ZIP file.
 * @param update `true` when an existing form should be updated.
 */
async function downloadFromLink(configs: IFormStoreConfig[], link: string, update?: boolean): Promise<void> {
  if (isEmpty(link)) {
    return;
  }
  if (!configs.some((config) => isStoreFormFileUrl(config, link))) {
    return;
  }
  loadingDialog(true);

  // Download store form via the client, as the server may not have access to
  // the internet.
  let base64: string;
  try {
    base64 = await downloadBinaryAsBase64(link);
  } catch (e) {
    handleErrorDownloadFailure(e, link);
    return;
  }

  // Send data to the backing bean to initiate the import
  // Remote command has built-in error handling
  const params = remoteCommandParams<["uploadStoreFile"]>();
  params.push({ name: "uploadData", value: base64 });
  if (update !== undefined) {
    params.push({ name: "updateForm", value: update });
  }
  await runRemoteCommand(BackendCommand?.uploadStoreFile, params);

  loadingDialog(false);
}

async function handlePasteFormStoreLink(event: JQuery.TriggeredEvent): Promise<void> {
  const e = parseRecord(event);
  if (e.originalEvent instanceof ClipboardEvent) {
    const text = e.originalEvent.clipboardData?.getData("text");
    await downloadFromLink([...KnownFormStores], parseString(text), undefined);
  }
}

function onPasteFormStoreLink(event: JQuery.TriggeredEvent): void {
  handlePasteFormStoreLink(event).catch((error) => console.error("Could not paste form store link", error));
}

/**
 * Adds the event listener for the paste event. Imports the form from the form
 * store when the user pastes a form store link via ctrl + V.
 */
export function initPasteStoreLink(): void {
  $(document).off("paste", onPasteFormStoreLink);
  $(document).on("paste", onPasteFormStoreLink);
}

export function returnToNewProjectDialog(): void {
  $(".new-project-dialog-wrapper").show();
  $(".new-project-search").show();
  $(".return-btn").hide();
  $(".ui-dialog-footer").removeClass("borderless");

  const iframe = $(".form-store-frame");
  const wrapper = $(".new-project-dialog-wrapper");
  wrapper.attr("style", "");
  wrapper.css("height", parseNumber(iframe.attr("height")));
  wrapper.css("width", parseNumber(iframe.attr("width")));
  iframe.hide();
}

/**
 * Sends a message to the form store (when open) with the currently installed
 * forms. Used when the form list is updated, e.g. when a new form was
 * installed. When no form store is open, no message is sent.
 * @param formDataJson Array with the currently installed forms.
 */
export function sendFormStatus(formDataJson: string): void {
  const iframe = $(".form-store-frame").get(0);
  if (iframe instanceof HTMLIFrameElement) {
    const formData = parseJsonArray(formDataJson);
    const origin = notEmpty(iframe.src) ? new URL(iframe.src).origin : "*";
    iframe.contentWindow?.postMessage(
      {
        payload: formData,
        type: "formcycle-form-state",
      },
      origin,
    );
  }
}

/**
 * Opens the form store in an iframe within the current content window. Called
 * when the user click on the new form card in the forms overview menu.
 * @param pfArgs The PrimeFaces arguments object with the data received from the
 * bean.
 */
export function insertFormStoreIframe(pfArgs: PrimeFaces.ajax.PrimeFacesArgs): void {
  // Read data we received from the bean
  const data = parseJsonObject(pfArgs.formStoreData);
  const formStoreId = parseString(data.formStoreId);
  const formStoreUrl = parseString(data.formStoreUrl);
  const fcVersion = parseString(data.fcVersion);
  const formData = parseArray(data.formData);

  // Remove previous iframes
  $(".form-store-frame").remove();

  // Create new iframe and set URL
  const iframe = $(document.createElement("iframe"));
  iframe.addClass("form-store-frame");
  iframe.data("storeId", formStoreId);
  iframe.data("formData", formData);
  iframe[0]?.addEventListener("error", (e) => {
    console.warn("Error while opening the form store dialog", e.error);
  });

  const url = new URL(formStoreUrl);
  url.searchParams.append("inline", window.location.origin);
  url.searchParams.append("xfc", fcVersion);
  iframe.attr("src", url.toString());

  // Append IFrame either to the new project dialog, or the new project page.
  const dialogContent = $byId("projectNewDialog:projectNewDialog_content");
  if (dialogContent.length > 0) {
    dialogContent.css("overflow", "hidden");
    dialogContent.append(iframe);
    $(".new-project-dialog-wrapper").hide();
    $(".new-project-search").hide();
    $(".return-btn").show();
    $(".ui-dialog-footer").addClass("borderless");
  } else {
    const content = $("#content");
    iframe.attr("height", parseString(content.outerHeight()));
    iframe.attr("width", parseString(content.outerWidth()));
    content.html("");
    content.append(iframe);
  }
}

export function init(id: string, path: string, formStoreUrl: string): void {
  if (isEmpty(formStoreUrl)) {
    return;
  }
  const url = new URL(formStoreUrl);
  const config: IFormStoreConfig = {
    formPath: path,
    id: id,
    url: url,
  };
  KnownFormStores.add(config);
  initFormStoreMessageListener(config);
}
